import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Risk, VipApi, RiskStatusEnum, RiskSeverityEnum } from 'api-client';
import { Shield, Tag, Clock, Calendar, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/Badge';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { getApiConfiguration } from '@/api/utils';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';


const getBadgeVariant = (value: string | undefined): "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" | "active" | "maintenance" | "critical" | "high" | "medium" | "low" | "open" | "resolved" | "in_progress" => {
  switch (value?.toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    case 'low':
      return 'low';
    case 'active':
      return 'active';
    case 'mitigated':
      return 'warning';
    case 'resolved':
      return 'success';
    default:
      return 'default';
  }
};

const RiskDetail = () => {
  const { id } = useParams();
  const [risk, setRisk] = useState<Risk | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentTeam } = useTeams();
  const teamSlug = currentTeam?.slug;
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !teamSlug || !token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const vipApi = new VipApi(getApiConfiguration(token));
        const riskResponse = await vipApi.risksRetrieve({
          id,
          teamSlug,
        });
        setRisk(riskResponse);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, teamSlug, token]);

  const handleUpdateRisk = async (field: keyof Risk, value: any) => {
    if (!risk || !teamSlug || !token) return;

    try {
      const vipApi = new VipApi(getApiConfiguration(token));
      const updatedRisk = await vipApi.vipRisksUpdate({
        teamSlug,
        id: String(risk.id),
        risk: {
          ...risk,
          [field]: value,
        }
      });
      setRisk(updatedRisk);
    } catch (error) {
      console.error('Failed to update risk:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!risk) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-400">
        Risk not found
      </div>
    );
  }

  const getSeverityIcon = (severity: RiskSeverityEnum | undefined) => {
    switch (severity) {
      case RiskSeverityEnum.Critical:
      case RiskSeverityEnum.High:
        return <AlertTriangle className="h-4 w-4" />;
      case RiskSeverityEnum.Medium:
      case RiskSeverityEnum.Low:
        return <Shield className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: RiskSeverityEnum | undefined) => {
    switch (severity) {
      case RiskSeverityEnum.Critical:
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case RiskSeverityEnum.High:
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case RiskSeverityEnum.Medium:
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case RiskSeverityEnum.Low:
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusColor = (status: RiskStatusEnum | undefined) => {
    switch (status) {
      case RiskStatusEnum.Active:
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case RiskStatusEnum.Mitigated:
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case RiskStatusEnum.Resolved:
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className={`p-3 rounded-lg ${getSeverityColor(risk.severity)}`}>
              {getSeverityIcon(risk.severity)}
            </div>
            <div className='space-y-4'>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">{risk.title}</h1>
              </div>
              {risk.link && (
                  <div className="flex items-center space-x-3">
                    <a 
                      href={risk.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                    >
                      {risk.link?.slice(0, 20)}...
                    </a>
                  </div>
                )}
              <div className="flex items-center space-x-4 mt-2">
                <Badge variant={getBadgeVariant(risk.severity)}>
                  {risk.severity?.toUpperCase()}
                </Badge>
                <Badge variant={getBadgeVariant(risk.status)}>
                  {risk.status?.toUpperCase()}
                </Badge>
                {risk.source && (
                  <span className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300">
                    {risk.source}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="space-y-2">
              <Select
                value={risk.severity}
                onValueChange={(value) => handleUpdateRisk('severity', value)}
              >
                <SelectTrigger className={`w-[180px] bg-gray-800/30 border border-gray-800 ${getSeverityColor(risk.severity)}`}>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1F2E] border border-gray-800">
                  {Object.values(RiskSeverityEnum).map((severity) => (
                    <SelectItem key={severity} value={severity} className="text-gray-300 focus:bg-gray-800/50 focus:text-white">
                      <div className="flex items-center">
                        {getSeverityIcon(severity as RiskSeverityEnum)}
                        <span className="ml-1">{severity}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Select
                value={risk.status}
                onValueChange={(value) => handleUpdateRisk('status', value)}
              >
                <SelectTrigger className={`w-[180px] bg-gray-800/30 border border-gray-800 ${getStatusColor(risk.status)}`}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1F2E] border border-gray-800">
                  {Object.values(RiskStatusEnum).map((status) => (
                    <SelectItem key={status} value={status} className="text-gray-300 focus:bg-gray-800/50 focus:text-white">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>{status}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="bg-[#0F1629] border-gray-800/50">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-white">Description</h2>
              <p className="text-gray-400 whitespace-pre-wrap">{risk.description}</p>
            </div>
          </Card>

          {/* Mitigation */}
          <Card className="bg-[#0F1629] border-gray-800/50">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-white">Mitigation</h2>
              <p className="text-gray-400 whitespace-pre-wrap">{risk.mitigation || 'No mitigation steps provided.'}</p>
            </div>
          </Card>
          
          <AssetNotes objectId={risk.id?.toString()} objectType="risk" />;

        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Details */}
          <Card className="bg-[#0F1629] border-gray-800/50">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-white">Details</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">VIP Profile</span>
                  <span className="text-white">{risk.profileName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Source</span>
                  <div className="flex items-center text-white">
                    {risk.favicon && (
                      <img 
                        src={risk.favicon} 
                        alt="Source favicon" 
                        className="h-4 w-4 mr-2" 
                      />
                    )}
                    <span>{risk.source || 'N/A'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Created</span>
                  <div className="flex items-center text-white">
                    <Calendar className="h-4 w-4 mr-2" />
                    {risk.createdAt && format(new Date(risk.createdAt), 'MMM d, yyyy')}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Updated</span>
                  <div className="flex items-center text-white">
                    <Calendar className="h-4 w-4 mr-2" />
                    {risk.updatedAt && format(new Date(risk.updatedAt), 'MMM d, yyyy')}
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Source Preview */}
          {(risk.thumbnail || risk.link) && (
            <Card className="bg-[#0F1629] border-gray-800/50">
              <div className="p-6">
                <h2 className="text-lg font-semibold mb-4 text-white">Source Preview</h2>
                <div className="flex flex-col items-center space-y-4">
                  {risk.thumbnail && (
                    <img 
                      src={risk.thumbnail} 
                      alt="Content thumbnail" 
                      className="rounded-md max-w-full h-auto" 
                    />
                  )}
                  {risk.link && (
                    <a 
                      href={risk.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                    >
                      <span>View original source</span>
                    </a>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskDetail;
